import moment from 'moment';
import { helpers } from 'vuelidate/lib/validators';

export function sleep(millis: number): Promise<number> {
    const res: Promise<number> = new Promise(resolve => {
        setTimeout(() => resolve(millis), millis);
    });
    return res;
}

export function isDate(x: any): x is Date {
    return x instanceof Date;
}
export function isString(x: any): x is string {
    return typeof x === 'string';
}
export function isNumber(x: any): x is number {
    return typeof x === 'number';
}

export function getUrl(baseUrl: string, path: string, queryParams?: any): string {
    let url = getFullPath(baseUrl, path);
    if (queryParams) {
        url += formatParams(queryParams);
    }
    return url;
}

function getFullPath(baseUrl: string, path: string): string {
    if (path.startsWith('http://') || path.startsWith('https://'))
        return path;
    if (baseUrl && baseUrl.length > 0) {
        let base = baseUrl;
        if (base.endsWith('/'))
            base = base.substr(0, base.length - 1);
        if (path.startsWith('/'))
            path = path.substr(1);

        return `${base}/${path}`;
    }
    return path;
}

function formatParams(params) {
    return '?' + Object  //{fromDate: Date(), toDate: Date(), xx: null }
        .keys(params) // [ 'fromDate', 'toDate', 'xx' ]
        .filter(key => params[key]) // [ 'fromDate', 'toDate' ]
        .map(key => key + '=' + getParamValue(params[key]))  // [ 'fromDate=2018-3-1', 'toDate='2018-3-31' ]
        .join('&');  // 'fromDate=2018-3-1&toDate='2018-3-31'
}

function getParamValue(param: number | Date | string): string {
    let val = param;
    if (isDate(param))
        val = formatDate(param);
    else if (isNumber(param))
        val = param.toString();
    else
        val = param;
    return encodeURIComponent(val);
}

function formatDate(dt: Date): string {
    return `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}`;
}

export function getDate(value: string): string | null {
    if (value)
        value = value.trim();
    if (!value)
        return null;
    if (value.length !== 10)
        return null;
    try {
        const sep1 = value.charAt(2);
        const sep2 = value.charAt(5);
        if ((sep1 !== '-' && sep1 !== '/' && sep1 !== '.' && sep1 !== ' ') ||
            (sep2 !== '-' && sep2 !== '/' && sep2 !== '.' && sep2 !== ' ')) {
            return null;
        }
        const format = `DD${sep1}MM${sep2}YYYY`;
        const momentValue = moment(value, format);
        if (moment.isMoment(momentValue) && momentValue.isValid())
            return momentValue.format('DD-MM-YYYY');
        return null;
    } catch (ex) {
        console.error(ex);
        return null;
    }
}

export function getDateTime(value: string): string | null {
    if (value)
        value = value.trim();
    if (!value)
        return null;
    if (value.length !== 19)
        return null;
    try {
        const sep1 = value.charAt(2);
        const sep2 = value.charAt(5);
        if ((sep1 !== '-' && sep1 !== '/' && sep1 !== '.' && sep1 !== ' ') ||
            (sep2 !== '-' && sep2 !== '/' && sep2 !== '.' && sep2 !== ' ')) {
            return null;
        }
        const sep3 = value.charAt(10).toLowerCase();
        if (sep3 !== ' ' && sep3 !== '_' && sep3 !== 't') {
            return null;
        }
        const sep4 = value.charAt(13);
        const sep5 = value.charAt(16);
        if ((sep4 !== ':' && sep4 !== '.' && sep4 !== ' ') ||
            (sep5 !== ':' && sep5 !== '.' && sep5 !== ' ')) {
            return null;
        }
        const format = `DD${sep1}MM${sep2}YYYY${sep3}HH${sep4}mm${sep5}ss`;
        const momentValue = moment(value, format);
        if (moment.isMoment(momentValue) && momentValue.isValid())
            return momentValue.format('DD-MM-YYYY HH:mm:ss');
        return null;
    } catch (ex) {
        console.error(ex);
        return null;
    }
}

export function validDate(value: string): boolean {
    if (value)
        value = value.trim();
    if (!value)
        return true;
    const dateValue = getDate(value);
    if (dateValue)
        return true;
    return false;
}

export function validDateTime(value: string): boolean {
    if (value)
        value = value.trim();
    if (!value)
        return true;
    const dateTimeValue = getDateTime(value);
    if (dateTimeValue)
        return true;
    return false;
}

export function validEmail(value: string) {
    if (!value) return true;
    const emailValidator = helpers.regex('string', /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/ig);
    const res = emailValidator(value);
    return res;
}

export function validAFM(value: string): boolean {
    if (value)
        value = value.trim();
    if (!value)
        return true;
    if (value.length !== 9)
        return false;
    const checkDigit: number = value.charCodeAt(8) - 48;
    let sum: number = 0;
    for (let i = 7; i >= 0; i--) {
        sum += (value.charCodeAt(i) - 48) << (8 - i);
    }
    let mod: number = sum % 11;
    if (mod === 10)
        mod = 0;
    return (mod === checkDigit);
}

export function validKad(value: string): boolean {
    if (!value)
        return true;
    if (value.length < 3 || value.length > 5)
        return false;
    const testStr: string = value.replace(/\./g, '');
    if (!testStr)
        return false;
    if (testStr.length < (value.length - 1))
        return false;
    try {
        const testNumber: number = Number(testStr);
        if (isNaN(testNumber))
            return false;
        if (testNumber <= 0)
            return false;
    } catch (ex) {
        return false;
    }
    return true;
}

